import { useEffect, useSyncExternalStore } from 'react';
import { supabase } from '../utils/supabase';
import { tokenBadges, type TokenBadge, type TokenInputs } from '../components/ut/tokens';
import { REGISTRATION_CHANGED_EVENT } from './useQuickRegister';

/**
 * Current token status for every player, shared by all UT cards: one fetch
 * per page load (refreshed after 2 minutes or a registration change), so a
 * screen of 40 cards doesn't run 40 queries.
 *
 * - Priority: registered for the next game with `using_token`.
 * - Shield: `players.shield_active` (+ protected_streak_value, current_streak);
 *   "on" while used for the next game (`shield_token_usage.is_active`).
 * - Injury: `players.injury_token_active`; returning once registered for the next game.
 * - Unpaid: `player_unpaid_games_view` (selected, unpaid, over 24h after kick-off).
 */
const STALE_MS = 2 * 60 * 1000;
const INACTIVE = new Set(['dropped_out', 'absent', 'no_show']);
const EMPTY: TokenBadge[] = [];

let badges = new Map<string, TokenBadge[]>();
let loadedAt = 0;
let inflight: Promise<void> | null = null;
const listeners = new Set<() => void>();

async function load(): Promise<void> {
  const nextRes = await supabase
    .from('games')
    .select('id, game_registrations(player_id, status, using_token)')
    .neq('status', 'completed')
    .gte('date', new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString())
    .order('date', { ascending: true })
    .limit(1)
    .maybeSingle();
  const next = nextRes.data as { id: string; game_registrations: { player_id: string; status: string; using_token: boolean | null }[] | null } | null;
  const [shieldRes, injuryRes, usageRes, unpaidRes] = await Promise.all([
    supabase.from('players').select('id, protected_streak_value, current_streak').eq('shield_active', true),
    supabase.from('players').select('id, injury_original_streak, injury_return_streak').eq('injury_token_active', true),
    next ? supabase.from('shield_token_usage').select('player_id').eq('game_id', next.id).eq('is_active', true) : Promise.resolve({ data: [] }),
    supabase.from('player_unpaid_games_view').select('player_id, unpaid_games_count'),
  ]);

  const inputs = new Map<string, TokenInputs>();
  const get = (id: string) => inputs.get(id) ?? (inputs.set(id, {}), inputs.get(id)!);
  const regs = (next?.game_registrations ?? []).filter((r) => !INACTIVE.has(r.status));
  const registered = new Set(regs.map((r) => r.player_id));
  for (const r of regs) if (r.using_token) get(r.player_id).priority = true;
  const shieldedNext = new Set(((usageRes.data ?? []) as { player_id: string }[]).map((u) => u.player_id));
  for (const p of (shieldRes.data ?? []) as { id: string; protected_streak_value: number | null; current_streak: number | null }[]) {
    if (p.protected_streak_value == null) continue;
    get(p.id).shield = { protectedStreak: p.protected_streak_value, currentStreak: p.current_streak ?? 0, usedForNextGame: shieldedNext.has(p.id) };
  }
  for (const p of (injuryRes.data ?? []) as { id: string; injury_original_streak: number | null; injury_return_streak: number | null }[]) {
    get(p.id).injury = { originalStreak: p.injury_original_streak ?? 0, returnStreak: p.injury_return_streak ?? 0, returning: registered.has(p.id) };
  }

  for (const p of (unpaidRes.data ?? []) as { player_id: string; unpaid_games_count: number | null }[]) {
    if (p.unpaid_games_count) get(p.player_id).unpaid = Number(p.unpaid_games_count);
  }

  const out = new Map<string, TokenBadge[]>();
  for (const [id, t] of inputs) {
    const b = tokenBadges(t);
    if (b.length) out.set(id, b);
  }
  badges = out;
  loadedAt = Date.now();
  listeners.forEach((l) => l());
}

function refresh(force = false) {
  if (inflight || (!force && Date.now() - loadedAt < STALE_MS)) return;
  inflight = load()
    .catch((e) => console.error('Token badges failed to load:', e))
    .finally(() => {
      inflight = null;
    });
}

const subscribe = (l: () => void) => {
  listeners.add(l);
  return () => listeners.delete(l);
};

if (typeof window !== 'undefined') window.addEventListener(REGISTRATION_CHANGED_EVENT, () => refresh(true));

/** A player's token badges (empty when they have none, or while loading). */
export function useTokenBadges(playerId: string | null | undefined): TokenBadge[] {
  const map = useSyncExternalStore(subscribe, () => badges);
  useEffect(() => {
    refresh();
  }, []);
  return (playerId && map.get(playerId)) || EMPTY;
}
